import { auth } from '../config/firebase';
import { config } from '../config';
import { TenantTier } from '../constants/tenantTiers';
import {
  ClientRow,
  SupportData,
  TimeRange,
  DashboardSummaryData,
  DashboardActivityData,
  ClientsActivityData,
  ClientState,
  ClientActivityData,
  RepsData,
  RepSeriesData,
  ClientUser,
  ClientUsersData,
  CreateClientUserPayload,
  InitialKnowledgeDraftData,
  InitialKnowledgeDraftPayload,
  KbAuditEnqueueResult,
  KbContentData,
  KbReadinessThresholds,
  KbSuggestionPayload,
  SuperadminActionItem,
  UsageReportFile
} from '../types/superadmin';

/** Four tenants read in parallel plus the workbook build; generous so a cold Lambda does not time out. */
const USAGE_REPORT_TIMEOUT_MS = 60000;

/** Largest page backend-tenant serves for a client roster (MAX_PAGE_SIZE). */
export const CLIENT_USERS_PAGE_LIMIT = 100;

/** Serialize a time range into the query string the API expects. */
function rangeQuery(range: TimeRange): string {
  if (range.key === 'custom' && range.from && range.to) {
    return `?range=custom&from=${range.from}&to=${range.to}`;
  }
  return `?range=${range.key}`;
}

/** A client the backend could not read; the rest of the response is still valid. */
export interface FailedClient {
  tenantId: string;
  error: string;
}

interface Envelope<T> {
  success: boolean;
  data: T;
  meta?: { generatedAt?: string; failed?: FailedClient[]; count?: number };
  message?: string;
  error?: string;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  /** Message shown when the super-admin allowlist rejects the request. */
  forbiddenMessage?: string;
  /** Opt-in timeout for bounded long-running or polling requests. */
  timeoutMs?: number;
}

/**
 * Authenticated request against the backend-tenant super-admin API. Attaches
 * the current Firebase ID token; surfaces a friendly message on failure so
 * pages can render an error state instead of crashing.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<Envelope<T>> {
  const { method = 'GET', body, forbiddenMessage = 'You do not have access to this data.', timeoutMs } =
    options;
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Your session has expired. Please sign in again.');
  }

  const token = await user.getIdToken();
  const controller = timeoutMs ? new AbortController() : null;
  const timeout = controller ? window.setTimeout(() => controller.abort(), timeoutMs) : null;
  let res: Response;
  try {
    res = await fetch(`${config.api.baseURL}/api/superadmin${path}`, {
      method,
      ...(controller ? { signal: controller.signal } : {}),
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' })
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });
  } catch (networkError: any) {
    if (networkError?.name === 'AbortError') {
      throw new Error('The server took too long to respond. Please try again.');
    }
    throw new Error('Could not reach the server. Check your connection and try again.');
  } finally {
    if (timeout !== null) window.clearTimeout(timeout);
  }

  const json = (await res.json().catch(() => ({}))) as Envelope<T>;
  if (!res.ok || json.success === false) {
    // Only the allowlist rejection is a blanket "no access"; every other 403
    // (e.g. trial access required) carries a message worth showing verbatim.
    if (res.status === 403 && json.error === 'FORBIDDEN') {
      throw new Error(forbiddenMessage);
    }
    throw new Error(json.message || `Request failed (${res.status}).`);
  }
  return json;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });
const patch = <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body });

export const api = {
  // Live analytics. Each page loads its CURRENT-state half (no range) and its
  // range-ACTIVITY half separately. Cross-client views take the tier.
  getDashboardSummary: (tier: TenantTier) => get<DashboardSummaryData>(`/dashboard/summary?tier=${tier}`),
  getDashboardActivity: (range: TimeRange, tier: TenantTier) =>
    get<DashboardActivityData>(`/dashboard/activity${rangeQuery(range)}&tier=${tier}`),
  getClients: (tier: TenantTier) => get<{ tier: TenantTier; clients: ClientRow[] }>(`/clients?tier=${tier}`),
  getClientsActivity: (range: TimeRange, tier: TenantTier) =>
    get<ClientsActivityData>(`/clients/activity${rangeQuery(range)}&tier=${tier}`),
  getClient: (tenantId: string) => get<{ client: ClientState }>(`/clients/${encodeURIComponent(tenantId)}`),
  getClientActivity: (tenantId: string, range: TimeRange) =>
    get<ClientActivityData>(`/clients/${encodeURIComponent(tenantId)}/activity${rangeQuery(range)}`),
  getReps: (range: TimeRange, tier: TenantTier) => get<RepsData>(`/reps${rangeQuery(range)}&tier=${tier}`),
  getRepSeries: (tenantId: string, userId: string, range: TimeRange) =>
    get<RepSeriesData>(
      `/clients/${encodeURIComponent(tenantId)}/reps/${encodeURIComponent(userId)}/series${rangeQuery(range)}`
    ),
  getSupport: (tier: TenantTier) => get<SupportData>(`/support?tier=${tier}`),
  // The production clients' usage summary for the range, as an .xlsx. Always
  // the prod tier: it is the report sent to the customers' account owners.
  getUsageReport: (range: TimeRange) =>
    request<UsageReportFile>(`/reports/usage${rangeQuery(range)}`, { timeoutMs: USAGE_REPORT_TIMEOUT_MS }),

  // Client user management.
  // One request for the whole roster so the merged users/reps table can sort
  // and page on the client across every user.
  getClientUsers: (tenantId: string) =>
    get<ClientUsersData>(
      `/clients/${encodeURIComponent(tenantId)}/users?limit=${CLIENT_USERS_PAGE_LIMIT}`
    ),
  createClientUser: (tenantId: string, payload: CreateClientUserPayload) =>
    post<{ user: ClientUser }>(`/clients/${encodeURIComponent(tenantId)}/users`, payload),
  deactivateClientUser: (tenantId: string, userId: string) =>
    del<{ user: ClientUser }>(
      `/clients/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`
    ),
  // The admin portal's status switch: active -> deactivated, or back.
  toggleClientUser: (tenantId: string, userId: string) =>
    post<{ user: ClientUser }>(
      `/clients/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/toggle`,
      {}
    ),
  // Permanent: Firebase account, tenant user, tenant mapping, authorization
  // and calendar integrations all go. Not reversible.
  deleteClientUser: (tenantId: string, userId: string) =>
    del<{ user: ClientUser }>(
      `/clients/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}?hardDelete=true`
    ),
  // Re-send one user's welcome email. The backend sets a fresh temporary
  // password (the original is unrecoverable) before emailing; it refuses
  // users who have already logged in.
  resendClientUserWelcome: (tenantId: string, userId: string) =>
    post<{ sentTo: string }>(
      `/clients/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/resend-welcome`,
      {}
    ),
  // Welcome email: {} = real send to the default admin (resets their temp
  // password); { testEmail } = QA render to that address, nothing modified.
  sendWelcomeEmail: (tenantId: string, payload: { testEmail?: string }) =>
    post<{ sentTo: string; isTest: boolean; adminEmail: string }>(
      `/clients/${encodeURIComponent(tenantId)}/welcome-email`,
      payload
    ),

  getInitialKnowledgeDraft: (tenantId: string) =>
    request<InitialKnowledgeDraftData>(`/clients/${encodeURIComponent(tenantId)}/knowledge-draft`, {
      timeoutMs: 15000
    }),
  prepareInitialKnowledgeDraft: (tenantId: string, payload: InitialKnowledgeDraftPayload) =>
    post<InitialKnowledgeDraftData & { created: boolean; retried: boolean }>(
      `/clients/${encodeURIComponent(tenantId)}/knowledge-draft`,
      payload
    ),

  // KB content: read live from the client's own database. Suggestions write
  // an Action document directly into that same database (type
  // 'kb_suggestion') for the client's admin to see.
  getKbContent: (tenantId: string) =>
    get<KbContentData>(`/clients/${encodeURIComponent(tenantId)}/kb-content`),
  saveKbReadinessThresholds: (tenantId: string, thresholds: KbReadinessThresholds) =>
    request<KbContentData>(`/clients/${encodeURIComponent(tenantId)}/kb-readiness-thresholds`, {
      method: 'PUT',
      body: { thresholds }
    }),
  runKbAuditNow: (tenantId: string) =>
    request<KbAuditEnqueueResult>(`/clients/${encodeURIComponent(tenantId)}/kb-audit/run`, {
      method: 'POST', body: {}
    }),
  sendKbSuggestion: (tenantId: string, payload: KbSuggestionPayload) =>
    post<{ action: unknown }>(`/clients/${encodeURIComponent(tenantId)}/kb-suggestions`, payload),

  // Cross-tenant Actions Center — notifications about clients, not scoped to
  // one tenant.
  getActions: (tier: TenantTier) => get<{ actions: SuperadminActionItem[] }>(`/actions?tier=${tier}`),
  getClientActions: (tenantId: string) =>
    get<{ actions: SuperadminActionItem[] }>(`/actions?tenantId=${encodeURIComponent(tenantId)}`),
  getActionsCount: (tier: TenantTier) => get<{ count: number }>(`/actions/count?tier=${tier}`),
  updateActionStatus: (id: string, status: 'dismissed' | 'completed') =>
    patch<{ action: SuperadminActionItem }>(`/actions/${encodeURIComponent(id)}`, { status })
};
